import AddIcon from '@mui/icons-material/Add'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import { Button, TablePagination } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { OrganizationSetupModal } from '@/app/organizations/components/OrganizationSetupModal'
import { PAGE_SIZE_OPTIONS } from '@/definitions/pagination'
import { useOrganizations } from '@/hooks/useOrganizations'
import { MainLayout, PageContent } from '@/layouts'

import { OrgList } from './components/OrgList'
import { OrgSearch } from './components/OrgSearch'
import {
  type DirectoryParams,
  nextDirectoryParams,
  parseDirectoryParams,
  toSearchParams,
} from './utils/directoryParams'
import { getOrgEmptyStateVariant } from './utils/getOrgEmptyStateVariant'

export const OrgDirectoryPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // The URL is the source of truth for page, page size, and search, so a
  // reload or a shared link lands on the same view.
  const params = parseDirectoryParams(searchParams)
  const { data, isLoading } = useOrganizations(params)

  const organizations = data?.results ?? []
  const totalCount = data?.total_count ?? 0

  const emptyStateVariant = getOrgEmptyStateVariant({
    totalCount,
    resultCount: organizations.length,
    hasSearch: Boolean(params.search),
  })
  // The URL can ask for a page past the end of the results, which TablePagination
  // rejects as out of range and labels nonsensically. The control is pinned to
  // the last page that holds rows while the list explains the mismatch.
  const lastPage = totalCount > 0 ? Math.ceil(totalCount / params.limit) - 1 : 0

  const updateParams = (changes: Partial<DirectoryParams>) => {
    setSearchParams(toSearchParams(nextDirectoryParams(params, changes)), {
      // Typing a search shouldn't leave a history entry per keystroke.
      replace: true,
    })
  }

  const handleSearchChange = (search: string) => updateParams({ search })

  const resetToFirstPage = () => updateParams({ page: 0 })

  const openCreateModal = () => setIsCreateModalOpen(true)

  return (
    <>
      <MainLayout
        title='Organizations'
        subtitle={isLoading ? undefined : `${totalCount} ${totalCount === 1 ? 'organization' : 'organizations'}`}
        search={<OrgSearch initialValue={params.search} onChange={handleSearchChange} />}
        actions={
          <>
            {/* The directory is the app's landing page, so the flag registry —
                which belongs to no one organization — is reached from here. */}
            <Button
              variant='outlined'
              size='medium'
              startIcon={<FlagOutlinedIcon />}
              onClick={() => navigate('/feature-flags')}
            >
              Feature Flags
            </Button>
            <Button
              variant='contained'
              size='medium'
              startIcon={<AddIcon />}
              onClick={openCreateModal}
            >
              New Organization
            </Button>
          </>
        }
      >
        <PageContent>
          <OrgList
            orgs={organizations}
            isLoading={isLoading}
            onCreateOrg={openCreateModal}
            onResetPage={resetToFirstPage}
            emptyStateVariant={emptyStateVariant}
          />
          {totalCount > 0 && (
            <TablePagination
              component='div'
              count={totalCount}
              page={Math.min(params.page, lastPage)}
              rowsPerPage={params.limit}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onPageChange={(_, page) => updateParams({ page })}
              onRowsPerPageChange={(event) =>
                updateParams({ limit: Number(event.target.value) })}
            />
          )}
        </PageContent>
      </MainLayout>

      {isCreateModalOpen && (
        <OrganizationSetupModal onClose={() => setIsCreateModalOpen(false)} />
      )}
    </>
  )
}
